import React, { useRef, useEffect } from 'react';
import './CalculusOfMeaning.css';

/* ── Neural Particle Network (reused) ── */
function NeuralParticles() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animId;
    const particles = [];
    const COUNT = 55;
    const CONNECT_DIST = 120;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    for (let i = 0; i < COUNT; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        r: Math.random() * 1.5 + 0.5,
        o: Math.random() * 0.25 + 0.08,
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < CONNECT_DIST) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(201,168,76,${(1 - d / CONNECT_DIST) * 0.07})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(201,168,76,${p.o})`;
        ctx.fill();
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < -10) p.x = canvas.width + 10;
        if (p.x > canvas.width + 10) p.x = -10;
        if (p.y < -10) p.y = canvas.height + 10;
        if (p.y > canvas.height + 10) p.y = -10;
      });
      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="calc-particles" />;
}

function Pulse() {
  return (
    <div className="calc-divider">
      <div className="calc-line" />
    </div>
  );
}

/* ── Main Page ── */
function CalculusOfSentiment() {
  return (
    <div className="calculus-page">
      {/* Hero */}
      <div className="calculus-hero">
        <video autoPlay muted playsInline className="calculus-hero-video" loop>
          <source src="/assets/calculus-hero.mp4" type="video/mp4" />
        </video>
        <div className="calculus-hero-overlay" />
        <div className="calculus-hero-content">
          <span className="calculus-hero-label">Prosody &middot; Pharmacokinetics &middot; The Gap Between Tone and Truth</span>
          <h1>The Calculus of Sentiment Analysis</h1>
          <p className="calculus-hero-sub">Sentiment analysis reads the cover. Neurochemical analysis reads the book.</p>
        </div>
      </div>

      {/* Content */}
      <div className="calculus-body" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/calculus-bg.png)` }}>
        <NeuralParticles />
        <div className="calculus-content">

          {/* Opening */}
          <section className="calc-section reveal">
            <p className="calc-lead">
              The voice analysis industry has settled on a comfortable answer to a shallow question.
              The question is: how does this person sound? The answer is: happy, sad, angry, neutral.
            </p>
            <p>
              Four labels. Maybe six, if the system is ambitious. Applied to a signal that carries
              the full bandwidth of human neurochemistry compressed into acoustic vibration.
            </p>
            <p>
              That is not analysis. That is rounding.
            </p>
          </section>

          <Pulse />

          {/* What Prosody Actually Measures */}
          <section className="calc-section reveal">
            <h2>What Prosody Actually Measures</h2>
            <p>
              Prosody analysis, the backbone of every commercial sentiment system, measures real things.
              Pitch contour. Speaking rate. Rhythm. Stress patterns. Intonation. Vocal energy.
              These are legitimate acoustic features. They correlate with emotional states. Nobody
              disputes that.
            </p>
            <p>
              The problem is what happens next.
            </p>
            <p>
              These acoustic features get mapped to categorical labels through a classifier trained on
              human-annotated datasets. Thousands of audio clips where someone listened and decided:
              this one sounds angry. This one sounds happy. This one sounds frustrated.
            </p>
            <p>
              The labels are opinions. They reflect what the annotator perceived, filtered through
              their own culture, language, mood, and biases. The model learns to replicate those
              opinions. It does not learn the underlying biology. It learns the surface pattern
              that a room full of annotators agreed looked like anger.
            </p>

            <div className="calc-thesis-box">
              <p>Prosody analysis measures how a person sounds. It does not measure what is happening inside them. These are not the same thing. They are not even close.</p>
            </div>
          </section>

          <Pulse />

          {/* The Performer Problem */}
          <section className="calc-section reveal">
            <h2>The Performer Problem</h2>
            <p>
              A patient calls their doctor's office. The receptionist asks how they're doing.
              The patient says "I'm doing great, thanks!" Their pitch is up. Their speaking rate
              is normal. Their intonation is bright. Every prosody classifier on earth will label
              this: positive sentiment. High confidence.
            </p>
            <p>
              The patient has been crying for three hours. Their serotonin baseline has been declining
              for six weeks. Their cortisol is chronically elevated. They haven't slept properly in
              eleven days. They are performing "fine" because that is what humans do when they are
              asked how they are by someone who does not have time for the real answer.
            </p>
            <p>
              The prosody says positive. The pharmacokinetics say crisis.
            </p>
            <p>
              This is not an edge case. This is the default human behavior. People perform wellness.
              They modulate their tone to match social expectations. They have been doing it since
              childhood, since the first time they learned that "I'm fine" ends the conversation
              faster than the truth.
            </p>
            <p>
              Prosody-based sentiment analysis is defeated by basic social conditioning. The signal
              it reads is the performance. The signal it misses is the biology underneath.
            </p>
          </section>

          <Pulse />

          {/* What the Voice Actually Carries */}
          <section className="calc-section reveal">
            <h2>What the Voice Actually Carries</h2>
            <p>
              The human voice is not just an acoustic signal carrying semantic content. It is a
              biological signal carrying neurochemical state, whether the speaker intends it or not.
            </p>

            <div className="calc-table-wrap">
              <table className="calc-table">
                <thead>
                  <tr>
                    <th>Acoustic Feature</th>
                    <th>Prosody Interpretation</th>
                    <th>Neurochemical Reality</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Elevated pitch</td>
                    <td>"Sounds excited or happy"</td>
                    <td>Adrenaline elevation. Could be excitement, fear, anxiety, or mania. The label collapses four distinct states into one.</td>
                  </tr>
                  <tr>
                    <td>Flattened pitch range</td>
                    <td>"Sounds bored or neutral"</td>
                    <td>Serotonin depletion. Could indicate clinical depression, emotional exhaustion, or dissociation. "Neutral" is a dangerous label for a person who has stopped being able to feel.</td>
                  </tr>
                  <tr>
                    <td>Increased speaking rate</td>
                    <td>"Sounds enthusiastic"</td>
                    <td>Dopamine surge with adrenaline co-activation. Could be genuine enthusiasm, anxiety-driven pressured speech, or stimulant effect. The prosody is identical. The pharmacokinetics are not.</td>
                  </tr>
                  <tr>
                    <td>Vocal tremor</td>
                    <td>"Sounds nervous"</td>
                    <td>Adrenaline-cortisol interaction affecting laryngeal muscle tension. The ratio of the two determines whether the tremor comes from stage fright, grief, cold rage, or neurological condition.</td>
                  </tr>
                  <tr>
                    <td>Vocal fry (creaky voice)</td>
                    <td>"Sounds disengaged"</td>
                    <td>Low subglottal pressure from reduced respiratory drive. Correlates with serotonin-dominant states: contentment, fatigue, or depressive withdrawal. Three clinically distinct states, one prosody label.</td>
                  </tr>
                  <tr>
                    <td>Extended pauses</td>
                    <td>"Sounds hesitant"</td>
                    <td>Cortisol-mediated inhibition of speech motor planning. The pause after a question about your marriage is neurochemically different from the pause when you're calculating a tip. Prosody sees the same silence.</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p>
              Every row in this table is a case where prosody sees one thing and the biology
              is doing something else entirely. The acoustic feature is real. The categorical
              interpretation is a guess. The neurochemical analysis is physics.
            </p>
          </section>

          <Pulse />

          {/* The Dimension Problem */}
          <section className="calc-section reveal">
            <h2>The Dimension Problem</h2>
            <p>
              Sentiment analysis maps a high-dimensional biological signal into a low-dimensional
              label space. This is information destruction, not information extraction.
            </p>

            <div className="calc-equation gold">
              <span className="calc-eq-label">Prosody Output</span>
              <code>voice &rarr; classifier &rarr; {"{"} angry | happy | sad | neutral {"}"}</code>
              <span className="calc-eq-desc">A continuous biological signal collapsed into 4 categories. 99.99% of the information discarded.</span>
            </div>

            <div className="calc-equation gold">
              <span className="calc-eq-label">Neurochemical Output</span>
              <code>voice &rarr; H-H cascade &rarr; [10D state vector with pharmacokinetic dynamics]</code>
              <span className="calc-eq-desc">The same signal decomposed into 10 neurochemical dimensions with temporal continuity, decay rates, and history.</span>
            </div>

            <p>
              The difference is not incremental. Going from 4 labels to a 10-dimensional continuous
              vector with pharmacokinetic dynamics is not "better sentiment analysis." It is a
              different category of measurement entirely.
            </p>
            <p>
              A thermometer tells you the room is warm. A full-spectrum infrared camera tells you
              which wall is losing heat, where the insulation failed, and whether the pipe behind
              the drywall is about to burst. Same physical phenomenon. Different resolution.
              Different utility. Different world.
            </p>
          </section>

          <Pulse />

          {/* The Temporal Problem */}
          <section className="calc-section reveal">
            <h2>The Temporal Problem</h2>
            <p>
              Sentiment analysis is stateless. Each utterance is classified independently. The system
              has no memory of what came before and no prediction of what comes next. It processes
              snapshots.
            </p>
            <p>
              Human neurochemistry is the opposite of stateless. It is defined by temporal dynamics.
              Cortisol has a half-life of 60 to 90 minutes. Oxytocin clears in 3 to 5 minutes.
              Dopamine spikes and crashes in seconds. Serotonin establishes baselines over weeks.
              These timescales interact. They overlap. They compound.
            </p>
            <p>
              When a person calls a crisis line at 2 AM, their neurochemical state is not determined
              by what they say in the first 30 seconds. It is determined by the cortisol that has
              been accumulating for days, the serotonin that has been declining for weeks, and the
              adrenaline that spiked when they finally picked up the phone. The first 30 seconds
              of prosody might sound calm. The pharmacokinetics say the system is at its limit.
            </p>

            <div className="calc-equation gold">
              <span className="calc-eq-label">Prosody: Memoryless</span>
              <code>sentiment(t) = classify(utterance(t))</code>
              <span className="calc-eq-desc">Each moment analyzed in isolation. No history. No trajectory. No decay.</span>
            </div>

            <div className="calc-equation gold">
              <span className="calc-eq-label">Neurochemical: History-Dependent</span>
              <code>dS&#7522;/dt = &minus;&lambda;&#7522; (S&#7522;(t) &minus; E&#7522;) + stimulus&#7522;(t)</code>
              <span className="calc-eq-desc">State evolves continuously. Each moment carries the weight of every moment before it. Decay rates match real pharmacokinetics.</span>
            </div>

            <p>
              You cannot understand a conversation through snapshots any more than you can understand
              a river by photographing individual droplets. The trajectory IS the meaning. Sentiment
              analysis throws the trajectory away and keeps the droplets.
            </p>
          </section>

          <Pulse />

          {/* The Scar Problem */}
          <section className="calc-section reveal">
            <h2>The Scar Problem</h2>
            <p>
              Some moments in a conversation change the participants permanently. The neurochemical
              impact exceeds a threshold and leaves a marker that never fully decays. A diagnosis
              delivered. A confession heard. A trust broken. A hand held in silence.
            </p>
            <p>
              Sentiment analysis has no concept of this. Every utterance is born equal and dies
              immediately. The system that classified "I'm leaving" as negative sentiment has
              already moved on to the next utterance. The human who received those words will
              carry the neurochemical scar for years.
            </p>
            <p>
              A system that processes human conversation without understanding that some moments
              are permanent is not processing conversation at all. It is processing audio.
            </p>
          </section>

          <Pulse />

          {/* The Cross-Cultural Problem */}
          <section className="calc-section reveal">
            <h2>The Cross-Cultural Problem</h2>
            <p>
              Prosody is culturally conditioned. What sounds "angry" in American English sounds
              "passionate" in Italian. What sounds "disengaged" in German sounds "respectful" in
              Japanese. Every sentiment classifier trained on English-labeled data carries English
              cultural assumptions about what emotions sound like.
            </p>
            <p>
              Neurochemistry is universal. Cortisol responds to threat identically regardless of
              the language the threat arrives in. Oxytocin releases during trust-building exchanges
              whether those exchanges happen in Mandarin, Arabic, or Swahili. The pharmacokinetics
              do not have a locale setting.
            </p>
            <p>
              This is not a philosophical distinction. It determines whether your system works
              in one language or every language. Prosody classifiers must be retrained for each
              culture. Neurochemical analysis works on the signal underneath culture, the biology
              that every human body speaks fluently regardless of what language the mouth produces.
            </p>
          </section>

          <Pulse />

          {/* What It Takes to Get There */}
          <section className="calc-section reveal">
            <h2>What It Takes to Get There</h2>
            <p>
              Reading the neurochemistry underneath the voice is harder than reading the tone on
              top of it. Orders of magnitude harder. It requires:
            </p>

            <div className="calc-implications">
              <div className="calc-implication">
                <h4>Biologically grounded gating, not learned classifiers.</h4>
                <p>Hodgkin-Huxley conductance dynamics with real pharmacokinetic half-lives, not
                neural networks trained on annotator opinions. The gate equations come from
                electrophysiology, not from labeled datasets.</p>
              </div>
              <div className="calc-implication">
                <h4>State that persists, decays, and scars.</h4>
                <p>A 10-dimensional neurochemical vector that evolves according to differential
                equations, with mean-reverting decay toward biological baselines, autoreceptor
                feedback, vesicle pool depletion, and metabolic energy constraints. Not a label
                that vanishes when the next audio frame arrives.</p>
              </div>
              <div className="calc-implication">
                <h4>Antagonistic pair dynamics.</h4>
                <p>Dopamine and serotonin compete. Cortisol and oxytocin suppress each other.
                Adrenaline and GABA push in opposite directions. The 10 dimensions are not
                independent. They are coupled through biological antagonism. Sentiment labels
                have no concept of neurochemical competition.</p>
              </div>
              <div className="calc-implication">
                <h4>Three nested timescales.</h4>
                <p>Fast gating dynamics in milliseconds. Medium-term vesicle depletion and
                autoreceptor feedback in seconds. Slow glial modulation of the time constants
                themselves in minutes. A neuron that has been firing heavily for ten minutes
                does not respond the same way to new input. Sentiment analysis treats every
                moment as the first moment.</p>
              </div>
            </div>

            <p>
              This is not something you add to prosody analysis. You cannot bolt pharmacokinetics
              onto a sentiment classifier and call it neurochemical. The architecture is fundamentally
              different. The input is the same, voice, but what you do with it after it enters the
              system determines whether you get a label or a living state vector.
            </p>
          </section>

          <Pulse />

          {/* The Output Difference */}
          <section className="calc-section reveal">
            <h2>The Output Difference</h2>

            <div className="calc-attempts">
              <div className="calc-attempt">
                <h4>What Sentiment Analysis Tells You</h4>
                <p>"The customer sounded frustrated during this call."</p>
              </div>
              <div className="calc-attempt">
                <h4>What Neurochemical Analysis Tells You</h4>
                <p>"The customer's cortisol has been climbing across three calls over two weeks. Their
                serotonin baseline is 34% below their personal norm. During this call, adrenaline spiked
                at the 4:12 mark when the agent mentioned the billing change, and did not clear for the
                remainder of the conversation. Oxytocin, which typically rises when this customer speaks
                with Agent Sarah, was absent for the first time. The trust trajectory has inverted.
                This customer is not frustrated. They are leaving."</p>
              </div>
            </div>

            <p>
              One of these outputs changes what happens next. The other confirms what already
              happened. One of these outputs is a weather report. The other is a forecast.
            </p>
          </section>

          <Pulse />

          {/* Closing */}
          <section className="calc-section calc-closing reveal">
            <p className="calc-closing-line"><em>Prosody reads the performance.</em></p>
            <p className="calc-closing-line"><em>Sentiment assigns the label.</em></p>
            <p className="calc-closing-line"><em>Neurochemistry reveals what the voice was actually carrying.</em></p>
            <p className="calc-closing-final">The calculus of sentiment analysis ends at the surface.<br />The calculus of neurochemistry begins where the surface lies.</p>
          </section>

          {/* Signature */}
          <div className="calc-signature reveal">
            <p><strong>Nexus Concordat Inc.</strong></p>
            <p>AETHER: Adaptive Endocrine Transformer Heads with Emotional Regulation</p>
            <div className="patent-tags">
              <span className="patent-tag">Patent Pending: 63/939,190</span>
              <span className="patent-tag">Patent Pending: 63/962,385</span>
              <span className="patent-tag">Patent Pending: 63/988,485</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default CalculusOfSentiment;
